// apps/provider/src/services/api/modmed.js
// ModMed (EMA + MMPM) practice-management integration methods.
//
// Methods: getModMedConfig, saveModMedConfig, updateModMedConfig, probeModMed,
//          syncModMedReferenceData, getModMedMappings, updateModMedMapping,
//          searchModMedPatients, linkModMedPatient, postChargesToModMed,
//          verifyModMedCharges, getModMedChargeLogs, triggerModMedPatientSync
//
// Response types: @prism-ehr/shared → ModMedConfig, ModMedMapping, ModMedChargeLog,
//                 ModMedChargeResult, ModMedVerifyResult, ModMedProbeResult,
//                 ModMedSyncResult, ModMedPatientSearchResult, ModMedPatientSyncResult

/**
 * @returns {Object} ModMed domain methods (bound to `this` of the composed apiService)
 */
export function createModMedMethods() {
  return {

    // ============ Configuration ============

    /**
     * Xano: GET /modmed-config (secrets masked)
     * @returns {Promise<import('@prism-ehr/shared').ModMedConfig | null>}
     */
    async getModMedConfig() {
      return this.request('/modmed-config', { method: 'GET' });
    },

    /**
     * Xano: POST /modmed-config
     * @returns {Promise<import('@prism-ehr/shared').ModMedConfig>}
     */
    async saveModMedConfig(config) {
      return this.request('/modmed-config', { method: 'POST', body: JSON.stringify(config) });
    },

    /**
     * Xano: PATCH /modmed-config (non-null fields only; masked secrets ignored)
     * @returns {Promise<import('@prism-ehr/shared').ModMedConfig>}
     */
    async updateModMedConfig(config) {
      return this.request('/modmed-config', { method: 'PATCH', body: JSON.stringify(config) });
    },

    /**
     * Sandbox discovery / Test Connection. mode = auth | locations | practitioners | payers |
     * patient_search | patient_read | coverage | inbound_charges | charge_read | verify | charge_post_dry
     *
     * Xano: POST /modmed-probe
     * @returns {Promise<import('@prism-ehr/shared').ModMedProbeResult>}
     */
    async probeModMed(params) {
      return this.request('/modmed-probe', { method: 'POST', body: JSON.stringify(params), timeoutMs: 120000 });
    },

    // ============ Reference data ============

    /**
     * Xano: POST /modmed-sync-reference-data
     * @param {string[]} [types] practitioners | locations (default both)
     * @returns {Promise<import('@prism-ehr/shared').ModMedSyncResult>}
     */
    async syncModMedReferenceData(types) {
      return this.request('/modmed-sync-reference-data', { method: 'POST', body: JSON.stringify({ types: types || ['practitioners', 'locations'] }), timeoutMs: 120000 });
    },

    /**
     * Xano: GET /modmed-mappings?mapping_type=
     * @returns {Promise<import('@prism-ehr/shared').ModMedMapping[]>}
     */
    async getModMedMappings(mappingType) {
      const qs = mappingType ? `?mapping_type=${encodeURIComponent(mappingType)}` : '';
      return this.request(`/modmed-mappings${qs}`, { method: 'GET' });
    },

    /**
     * Xano: PATCH /modmed-mappings/{mapping_id}
     * @returns {Promise<import('@prism-ehr/shared').ModMedMapping>}
     */
    async updateModMedMapping(mappingId, data) {
      return this.request(`/modmed-mappings/${mappingId}`, { method: 'PATCH', body: JSON.stringify(data) });
    },

    // ============ Patients ============

    /**
     * Xano: POST /modmed-search-patients
     * @returns {Promise<import('@prism-ehr/shared').ModMedPatientSearchResult[]>}
     */
    async searchModMedPatients(searchParams) {
      return this.request('/modmed-search-patients', { method: 'POST', body: JSON.stringify(searchParams) });
    },

    /**
     * Xano: POST /modmed-link-patient
     * @returns {Promise<{success: boolean}>}
     */
    async linkModMedPatient(patientId, pmPatientId) {
      return this.request('/modmed-link-patient', { method: 'POST', body: JSON.stringify({ patient_id: patientId, pm_patient_id: pmPatientId }) });
    },

    /**
     * Xano: POST /modmed-trigger-patient-sync
     * @returns {Promise<import('@prism-ehr/shared').ModMedPatientSyncResult>}
     */
    async triggerModMedPatientSync() {
      return this.request('/modmed-trigger-patient-sync', { method: 'POST', body: JSON.stringify({}), timeoutMs: 180000 });
    },

    // ============ Charges ============

    /**
     * Post one encounter's charges to ModMed as a single ChargeItem. Same contract as
     * postChargesToVeradigmPM so the review modal only branches on the target.
     *
     * Xano: POST /modmed-post-charges
     * @returns {Promise<import('@prism-ehr/shared').ModMedChargeResult>}
     */
    async postChargesToModMed(encounterId, insuranceClaimId, finalizeEncounter, policyCoverage) {
      const body = { encounter_id: encounterId };
      if (insuranceClaimId) body.insurance_claim_id = insuranceClaimId;
      if (finalizeEncounter) body.finalize_encounter = true;
      if (policyCoverage) body.policy_coverage = policyCoverage;
      return this.request('/modmed-post-charges', { method: 'POST', body: JSON.stringify(body), timeoutMs: 180000 });
    },

    /**
     * Xano: POST /modmed-verify-charges
     * @returns {Promise<import('@prism-ehr/shared').ModMedVerifyResult>}
     */
    async verifyModMedCharges(insuranceClaimId) {
      return this.request('/modmed-verify-charges', { method: 'POST', body: JSON.stringify({ insurance_claim_id: insuranceClaimId }) });
    },

    /**
     * Xano: GET /modmed-charge-logs (paged envelope: items, itemsTotal, curPage, ...)
     * @returns {Promise<{items: import('@prism-ehr/shared').ModMedChargeLog[], itemsTotal: number}>}
     */
    async getModMedChargeLogs(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
      });
      const qs = params.toString();
      return this.request(`/modmed-charge-logs${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },
  };
}
